import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { C, FONT } from './styles';

/**
 * A full-screen overlay for the pallet check (the enlarged packing / part pictures, "Something's wrong").
 * role="dialog" + aria-modal, so the USB scan wedge leaves it alone; Escape and the 44 px close button
 * close it; Tab stays inside it and focus returns to what opened it; the page under it doesn't scroll. `dark` = the picture viewer, else the light form.
 */
const FOCUSABLE = 'a[href], button, input, textarea, select, [tabindex]:not([tabindex="-1"])';

export default function Overlay({ open, onClose, title, subtitle, dark = true, monoTitle = true, headerStyle = null, closeLabel = 'Close', children, testId = 'pallet-check-overlay' }) {
  const closeRef = useRef(null);
  const boxRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Escape closes; Tab stays inside while it is open; focus goes back to whatever opened it.
  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    const onKey = (e) => {
      if (e.key === 'Escape' && onCloseRef.current) { onCloseRef.current(); return; }
      if (e.key !== 'Tab' || !boxRef.current) return;
      const items = [...boxRef.current.querySelectorAll(FOCUSABLE)].filter((el) => !el.disabled && el.type !== 'file' && el.getAttribute('aria-hidden') !== 'true');
      if (!items.length) { e.preventDefault(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      const inside = boxRef.current.contains(document.activeElement);
      if (e.shiftKey && (document.activeElement === first || !inside)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (document.activeElement === last || !inside)) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const t = setTimeout(() => { if (closeRef.current) closeRef.current.focus(); }, 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus();
    };
  }, [open]);

  if (!open) return null;
  const bg = dark ? C.night : C.paper;
  const fg = dark ? '#FFFFFF' : C.ink;
  return createPortal(
    <div ref={boxRef} role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined} data-testid={testId}
      style={{ position: 'fixed', inset: 0, zIndex: 99995 /* over the floating feedback button (99990-99993) */, background: bg, color: fg, fontFamily: FONT, overflowY: 'auto',
        WebkitOverflowScrolling: 'touch' }}>
      <div style={{ maxWidth: 520, margin: '0 auto', minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, paddingTop: 'max(12px, env(safe-area-inset-top))', ...(headerStyle || {}) }}>
          <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <div style={{ fontFamily: monoTitle ? C.mono : 'inherit', fontSize: monoTitle ? 18 : 17, fontWeight: 700, overflowWrap: 'anywhere' }}>{title}</div>
            {subtitle && <div style={{ fontFamily: monoTitle ? 'inherit' : C.mono, fontSize: 13, color: headerStyle && headerStyle.color ? headerStyle.color : (dark ? C.dim : C.sub) }}>{subtitle}</div>}
          </div>
          <button ref={closeRef} type="button" aria-label={closeLabel} onClick={onClose}
            style={{ width: 44, height: 44, flexShrink: 0, borderRadius: 999, border: 0, background: dark ? '#2A2E35' : 'rgba(0,0,0,0.12)',
              color: headerStyle && headerStyle.color ? headerStyle.color : fg, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
